import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import jsPDF from 'jspdf'

import { format, utcToZonedTime } from 'date-fns-tz'
import { Typography, Box, Divider, Button } from '@mui/material'

import { GetApp, Refresh } from '@mui/icons-material'
import { IconButtonBg } from '@/theme/icon-button.theme'
import { TimeZoneUTCType, TimeZoneType } from '@/enums'

type Props = {
  email: string
  backupCodes: string[]
  handleGenerateBackupCodes: () => void
  handleHideAction: () => void
}

const BackupCodesComponent = ({
  email,
  backupCodes,
  handleGenerateBackupCodes,
  handleHideAction,
}: Props) => {
  const { t } = useTranslation()

  const getEmptyRows = (emptyRows: number) => {
    const emptyCode = []
    for (let index = 0; index < emptyRows; index += 1) {
      emptyCode.push(
        <Box flex="1 1 50%" p={2} pb={1.5} key={uuidv4()}>
          <Typography className="fontWeight-500" align="center">
            {'- - - - - -'}
          </Typography>
        </Box>,
      )
    }
    return emptyCode
  }

  const handleDownloadCodesAsPDF = () => {
    const doc = new jsPDF() // eslint-disable-line

    const todayUTC = utcToZonedTime(
      new Date(),
      TimeZoneUTCType[TimeZoneType.GMT_0],
    )

    let subIndex = 0
    doc.setFont('Avenir', 'bold')
    doc.setFontSize(18)
    doc.text(t('twoFactor.secretCodes'), 10, 15)
    doc.setFontSize(12)
    doc.setFont('Avenir', 'normal')
    doc.text(`${format(todayUTC, 'd MMM - H:mm')} UTC`, 165, 15)
    doc.setFont('Avenir', 'bold')
    doc.setFontSize(14)
    doc.text(`${t('twoFactor.codeForPlayer')} ${email}`, 10, 25)
    doc.setDrawColor(10, 10, 10)
    doc.line(10, 35, 200, 35)
    doc.setFontSize(12)
    doc.setFont('Avenir', 'normal')
    doc.text(t('twoFactor.keepCodesMessage'), 10, 47)
    doc.setFontSize(13)
    for (let index = 0; index < backupCodes.length; index += 1) {
      if (index < 5) {
        doc.text(`${index + 1}. ${backupCodes[index]}`, 13, 62 + 7 * subIndex)
      } else {
        doc.text(`${index + 1}. ${backupCodes[index]}`, 55, 62 + 7 * subIndex)
      }
      subIndex += 1
      if (subIndex === 5) subIndex = 0
    }
    doc.setFontSize(12)
    doc.text(`- ${t('twoFactor.eachCodeMessage')}`, 10, 105)
    doc.setDrawColor(10, 10, 10)
    doc.line(10, 115, 200, 115)
    doc.save('backup-codes.pdf')
  }

  return (
    <>
      <Box my={2}>
        <Box display="flex" justifyContent="space-between" pt={1} pb={3} pr={2}>
          <Typography className="fontWeight-500">
            {t('twoFactor.countAlternativeCodes', {
              number: backupCodes.length,
            })}
          </Typography>
          <IconButtonBg onClick={handleGenerateBackupCodes}>
            <Refresh style={{ fontSize: 18 }} />
          </IconButtonBg>
        </Box>
        <Divider light />
        <Box maxWidth={400} m="0px auto" display="flex" flexWrap="wrap" py={2}>
          {backupCodes.length < 10 && getEmptyRows(10 - backupCodes.length)}
          {backupCodes.map((code) => {
            return (
              <Box flex="1 1 50%" p={2} pb={1.5} key={uuidv4()}>
                <Typography className="fontWeight-500" align="center">
                  {code}
                </Typography>
              </Box>
            )
          })}
        </Box>
      </Box>
      <Box display="flex" flexDirection="row-reverse" mt={2}>
        <Box ml={2}>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={() => handleDownloadCodesAsPDF()}
            startIcon={<GetApp />}
          >
            {t('twoFactor.downloadCodes')}
          </Button>
        </Box>
        <Button
          className="red"
          variant="contained"
          size="small"
          onClick={handleHideAction}
        >
          {t('labels.back')}
        </Button>
      </Box>
    </>
  )
}

export default BackupCodesComponent
